import { useEffect, useRef } from 'react';

/** The first retry waits this long; each one after waits twice as long. */
export const FIRST_RETRY_MS = 2_000;
const LONGEST_RETRY_MS = 60_000;

/**
 * Reads a failed query again, after a pause that doubles each time up to a
 * minute, until it succeeds. The sidebar's reads are not repeated on focus
 * or on mount, so without this one failed read would last the session.
 */
export function useRetryOnError(
  { isError, isSuccess }: { isError: boolean; isSuccess: boolean },
  refetch: () => unknown
): void {
  const attempts = useRef(0);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  if (isSuccess) attempts.current = 0;
  useEffect(() => {
    if (!isError) return;
    const delay = Math.min(
      LONGEST_RETRY_MS,
      FIRST_RETRY_MS * 2 ** attempts.current
    );
    const timer = window.setTimeout(() => {
      attempts.current += 1;
      void refetchRef.current();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isError]);
}
